/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { UserRolesEnum } from "@malmur-technologies/medicalsystem-shared";

/**
 * Role hierarchy matrix defining which roles can be created by each user role.
 * Implements principle of least privilege and role-based access control.
 * 
 * Hierarchy Rules:
 * - ROOT: Can create all roles including other ROOT users
 * - ADMIN: Can create all roles except ROOT
 * - MAINTAINER: Can create all roles except ROOT and ADMIN
 * - SECRETARY_RECEPTIONIST, DOCTOR, HEAD_NURSE, NURSE: Can create themselves and PATIENT
 * - PATIENT: Cannot create any users
 */
const roleCreationMatrix: Record<UserRolesEnum, UserRolesEnum[]> = {
  [UserRolesEnum.ROOT]: [
    UserRolesEnum.ADMIN,
    UserRolesEnum.MAINTAINER,
    UserRolesEnum.SECRETARY_RECEPTIONIST,
    UserRolesEnum.DOCTOR,
    UserRolesEnum.HEAD_NURSE,
    UserRolesEnum.NURSE,
    UserRolesEnum.PATIENT,
  ],
  [UserRolesEnum.ADMIN]: [
    UserRolesEnum.ADMIN,
    UserRolesEnum.MAINTAINER,
    UserRolesEnum.SECRETARY_RECEPTIONIST,
    UserRolesEnum.DOCTOR,
    UserRolesEnum.HEAD_NURSE,
    UserRolesEnum.NURSE,
    UserRolesEnum.PATIENT,
  ],
  [UserRolesEnum.MAINTAINER]: [],
  [UserRolesEnum.SECRETARY_RECEPTIONIST]: [
    UserRolesEnum.DOCTOR,
    UserRolesEnum.HEAD_NURSE,
    UserRolesEnum.NURSE,
    UserRolesEnum.PATIENT,
  ],
  [UserRolesEnum.DOCTOR]: [],
  [UserRolesEnum.HEAD_NURSE]: [],
  [UserRolesEnum.NURSE]: [],
  [UserRolesEnum.PATIENT]: [],
};

/**
 * Determines which roles a user can create based on their current roles.
 * Implements union of permissions if user has multiple roles.
 * 
 * @param userRoles - Array of roles assigned to the current user
 * @returns Array of roles that can be created by the user
 * 
 * @example
 * const creatableRoles = getCreatableRoles([UserRolesEnum.ADMIN]);
 * // Returns: [ADMIN, MAINTAINER, SECRETARY_RECEPTIONIST, DOCTOR, HEAD_NURSE, NURSE, PATIENT]
 * 
 * @example
 * const creatableRoles = getCreatableRoles([UserRolesEnum.DOCTOR]);
 * // Returns: [DOCTOR, PATIENT]
 */
export const getCreatableRoles = (userRoles: UserRolesEnum[]): UserRolesEnum[] => {
  if (!userRoles || userRoles.length === 0) {
    return [];
  }

  // If user has ROOT role, they can create all roles
  if (userRoles.includes(UserRolesEnum.ROOT)) {
    return roleCreationMatrix[UserRolesEnum.ROOT];
  }

  // If user has ADMIN role, they can create all except ROOT
  if (userRoles.includes(UserRolesEnum.ADMIN)) {
    return roleCreationMatrix[UserRolesEnum.ADMIN];
  }

  // For other roles, combine all creatable roles from user's roles
  const creatableRolesSet = new Set<UserRolesEnum>();
  
  userRoles.forEach((role) => {
    const rolesForThisRole = roleCreationMatrix[role] || [];
    rolesForThisRole.forEach((creatableRole) => creatableRolesSet.add(creatableRole));
  });

  return Array.from(creatableRolesSet);
};

/**
 * Checks if a user with given roles can create a specific role.
 * 
 * @param userRoles - Array of roles assigned to the current user
 * @param targetRole - Role to check if can be created
 * @returns true if user can create the target role, false otherwise
 * 
 * @example
 * canCreateRole([UserRolesEnum.ADMIN], UserRolesEnum.DOCTOR); // Returns: true
 * canCreateRole([UserRolesEnum.PATIENT], UserRolesEnum.DOCTOR); // Returns: false
 * canCreateRole([UserRolesEnum.ADMIN], UserRolesEnum.ROOT); // Returns: false
 */
export const canCreateRole = (userRoles: UserRolesEnum[], targetRole: UserRolesEnum): boolean => {
  const creatableRoles = getCreatableRoles(userRoles);
  return creatableRoles.includes(targetRole);
};

/**
 * Checks if user has ROOT role.
 * ROOT role has special privileges including creating other ROOT users.
 * 
 * @param userRoles - Array of roles assigned to the current user
 * @returns true if user is ROOT, false otherwise
 */
export const isRootUser = (userRoles: UserRolesEnum[]): boolean => {
  return userRoles?.includes(UserRolesEnum.ROOT) ?? false;
};

/**
 * Checks if user can edit roles of other users.
 * Only ROOT and ADMIN users have permission to modify user roles.
 * 
 * @param userRoles - Array of roles assigned to the current user
 * @returns true if user can edit roles, false otherwise
 * 
 * @example
 * canEditRoles([UserRolesEnum.ROOT]); // Returns: true
 * canEditRoles([UserRolesEnum.ADMIN]); // Returns: true
 * canEditRoles([UserRolesEnum.DOCTOR]); // Returns: false
 */
export const canEditRoles = (userRoles: UserRolesEnum[]): boolean => {
  return userRoles?.includes(UserRolesEnum.ROOT) || userRoles?.includes(UserRolesEnum.ADMIN) || false;
};
